/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * SearchableSelect — Premium Searchable Dropdown
 * ============================================================================
 *
 * Uses @radix-ui/react-popover for portal rendering.
 * Features: brand accent trigger, staggered option animations, animated
 * checkmark, round clear pill, deep shadows, prefers-reduced-motion support.
 *
 * ============================================================================
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Search, ChevronDown, X, Check, Loader2, AlertCircle } from 'lucide-react';

/** Max option rows rendered at once; the rest require narrowing the search. */
const RENDER_LIMIT = 100;

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Extra text matched by the search box but never displayed (e.g. a barcode). */
  keywords?: string;
}

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  isLoading?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  noResultsText?: string;
  renderOption?: (option: SelectOption, isSelected: boolean) => React.ReactNode;
  required?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  label,
  error,
  helperText,
  isLoading = false,
  disabled = false,
  clearable = false,
  className = '',
  noResultsText = 'No results found',
  renderOption,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const matchingOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.description?.toLowerCase().includes(query) ||
        opt.keywords?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Render at most RENDER_LIMIT rows. A real product catalogue runs to thousands
  // of entries, and every row here is an animated DOM node — rendering them all
  // locks the browser for seconds when the dropdown opens. Slicing the list the
  // component actually uses (rather than only the JSX) keeps keyboard navigation
  // indexes aligned with what is on screen.
  const filteredOptions = useMemo(
    () => matchingOptions.slice(0, RENDER_LIMIT),
    [matchingOptions]
  );
  const hiddenCount = matchingOptions.length - filteredOptions.length;

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement;
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            const option = filteredOptions[highlightedIndex];
            if (!option.disabled) {
              onChange(option.value);
              closeDropdown();
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown();
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) => {
              let index = prev < filteredOptions.length - 1 ? prev + 1 : 0;
              while (filteredOptions[index]?.disabled && index < filteredOptions.length - 1) index++;
              return filteredOptions[index]?.disabled ? prev : index;
            });
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) => {
              let index = prev > 0 ? prev - 1 : filteredOptions.length - 1;
              while (filteredOptions[index]?.disabled && index > 0) index--;
              return filteredOptions[index]?.disabled ? prev : index;
            });
          }
          break;
        case 'Tab':
          closeDropdown();
          break;
      }
    },
    [disabled, isOpen, highlightedIndex, filteredOptions, onChange, closeDropdown]
  );

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    closeDropdown();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (isOpen) closeDropdown();
      else {
        setIsOpen(true);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    }
  };

  const defaultRenderOption = (option: SelectOption, isSelected: boolean) => (
    <div className="ss-opt-content">
      {option.icon && <span className="ss-opt-icon-box">{option.icon}</span>}
      <div className="ss-opt-text">
        <span className="ss-opt-label">{option.label}</span>
        {option.description && (
          <span className="ss-opt-desc">{option.description}</span>
        )}
      </div>
      {isSelected && (
        <span className="ss-opt-check">
          <Check className="ss-check-icon" />
        </span>
      )}
    </div>
  );

  return (
    <>
      <style>{ssStyles}</style>
      <div
        className={`ss-root ${className} ${disabled ? 'ss-disabled' : ''} ${error ? 'ss-has-error' : ''}`}
        onKeyDown={handleKeyDown}
      >
        {label && (
          <label className="ss-label">
            {label}
            {required && <span className="ss-required">*</span>}
          </label>
        )}

        <Popover.Root
          open={isOpen}
          modal={false}
          onOpenChange={(open) => { if (!open) closeDropdown(); }}
        >
          <Popover.Anchor asChild>
            <div
              className={`ss-trigger ${isOpen ? 'ss-open' : ''} ${error ? 'ss-error' : ''}`}
              onClick={toggleDropdown}
              tabIndex={disabled ? -1 : 0}
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-disabled={disabled}
            >
              {/* Brand accent line at bottom when open */}
              <div className="ss-trigger-accent" />

              <div className="ss-value">
                {selectedOption ? (
                  <span className="ss-selected">
                    {selectedOption.icon && (
                      <span className="ss-selected-icon">{selectedOption.icon}</span>
                    )}
                    {selectedOption.label}
                  </span>
                ) : (
                  <span className="ss-placeholder">{placeholder}</span>
                )}
              </div>

              <div className="ss-actions">
                {isLoading && <Loader2 className="ss-loader" />}
                {clearable && value && !disabled && (
                  <button
                    type="button"
                    className="ss-clear"
                    onClick={handleClear}
                    aria-label="Clear selection"
                  >
                    <X className="ss-clear-x" />
                  </button>
                )}
                <div className="ss-chevron-wrap">
                  <ChevronDown className={`ss-chevron ${isOpen ? 'ss-chevron-up' : ''}`} />
                </div>
              </div>
            </div>
          </Popover.Anchor>

          <Popover.Portal>
            <Popover.Content
              className="ss-dropdown"
              align="start"
              sideOffset={4}
              avoidCollisions
              collisionPadding={8}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={() => closeDropdown()}
              style={{ width: 'var(--radix-popover-anchor-width)' }}
            >
              {/* Search bar */}
              <div className="ss-search">
                <div className="ss-search-icon-wrap">
                  <Search className="ss-search-icon" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  placeholder={searchPlaceholder}
                  className="ss-search-input"
                  aria-label="Search options"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="ss-search-clear"
                    onClick={() => {
                      setSearchQuery('');
                      inputRef.current?.focus();
                    }}
                  >
                    <X className="ss-search-clear-x" />
                  </button>
                )}
              </div>

              {/* Options */}
              <ul
                ref={listRef}
                className="ss-options"
                role="listbox"
                aria-label={label || 'Options'}
              >
                {filteredOptions.length === 0 ? (
                  <li className="ss-empty">
                    <AlertCircle className="ss-empty-icon" />
                    <span>{noResultsText}</span>
                  </li>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = option.value === value;
                    const isHighlighted = index === highlightedIndex;

                    return (
                      <li
                        key={option.value}
                        className={`ss-option ${isSelected ? 'ss-selected-opt' : ''} ${isHighlighted ? 'ss-highlighted' : ''} ${option.disabled ? 'ss-opt-disabled' : ''}`}
                        onClick={() => handleSelect(option)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={option.disabled}
                        style={{ animationDelay: `${index * 20}ms` }}
                      >
                        {renderOption
                          ? renderOption(option, isSelected)
                          : defaultRenderOption(option, isSelected)}
                      </li>
                    );
                  })
                )}
                {hiddenCount > 0 && (
                  <li className="ss-more-hint">
                    {`+${hiddenCount} more — keep typing to narrow`}
                  </li>
                )}
              </ul>

              {/* Result count footer */}
              {filteredOptions.length > 0 && searchQuery && (
                <div className="ss-footer">
                  {filteredOptions.length} result{filteredOptions.length !== 1 ? 's' : ''}
                </div>
              )}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {(error || helperText) && (
          <p className={`ss-message ${error ? 'ss-msg-error' : ''}`}>
            {error || helperText}
          </p>
        )}
      </div>
    </>
  );
};

/* ─── Styles ─── */
const ssStyles = `
  /* ── Root ── */
  .ss-root {
    position: relative;
    width: 100%;
  }
  .ss-disabled {
    opacity: 0.55;
    pointer-events: none;
  }

  /* ── Label ── */
  .ss-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-secondary);
    margin-bottom: 6px;
  }
  .ss-required {
    color: var(--color-error);
    margin-left: 3px;
  }

  /* ── Trigger ── */
  .ss-trigger {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 44px;
    padding: 0 12px;
    background: var(--color-bg-tertiary);
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
    border-radius: 3px;
    cursor: pointer;
    transition: border-color 200ms cubic-bezier(0.16,1,0.3,1),
                box-shadow 200ms cubic-bezier(0.16,1,0.3,1),
                background 200ms ease;
    overflow: hidden;
  }

  .ss-trigger:hover:not(.ss-error) {
    border-color: var(--color-border-strong, rgba(255,255,255,0.18));
    background: var(--color-bg-hover);
  }

  .ss-trigger:focus-visible {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }

  .ss-trigger.ss-open {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px var(--color-brand-glow, rgba(139,0,0,0.15));
  }

  .ss-trigger.ss-error {
    border-color: var(--color-error);
  }
  .ss-trigger.ss-error.ss-open {
    box-shadow: 0 0 0 3px var(--color-error-light);
  }

  /* Accent line — slides in from left when open */
  .ss-trigger-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 0;
    background: var(--color-brand-primary);
    border-radius: 0;
    transition: width 300ms cubic-bezier(0.16,1,0.3,1);
  }
  .ss-trigger.ss-open .ss-trigger-accent {
    width: 100%;
  }

  /* ── Value display ── */
  .ss-value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    padding: 8px 0;
  }

  .ss-selected {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ss-selected-icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .ss-placeholder {
    font-size: 14px;
    color: var(--color-text-muted);
  }

  /* ── Actions (clear, loader, chevron) ── */
  .ss-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: 8px;
    flex-shrink: 0;
  }

  .ss-loader {
    width: 16px;
    height: 16px;
    color: var(--color-text-muted);
    animation: ss-spin 1s linear infinite;
  }

  @keyframes ss-spin {
    to { transform: rotate(360deg); }
  }

  .ss-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background: var(--color-bg-hover);
    border: none;
    border-radius: 3px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 180ms ease;
  }
  .ss-clear:hover {
    background: var(--color-error-light);
    color: var(--color-error);
    transform: scale(1.1);
  }
  .ss-clear-x { width: 12px; height: 12px; }

  .ss-chevron-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 3px;
    transition: background 180ms ease;
  }
  .ss-trigger:hover .ss-chevron-wrap {
    background: rgba(255,255,255,0.04);
  }
  [data-theme="light"] .ss-trigger:hover .ss-chevron-wrap {
    background: rgba(0,0,0,0.04);
  }

  .ss-chevron {
    width: 16px;
    height: 16px;
    color: var(--color-text-muted);
    transition: transform 250ms cubic-bezier(0.16,1,0.3,1),
                color 180ms ease;
  }
  .ss-chevron.ss-chevron-up {
    transform: rotate(180deg);
    color: var(--color-brand-primary);
  }

  /* ── Dropdown ── */
  .ss-dropdown {
    background: var(--color-bg-secondary);
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
    border-radius: 3px;
    box-shadow:
      0 12px 40px rgba(0,0,0,0.35),
      0 4px 12px rgba(0,0,0,0.15);
    overflow: hidden;
    animation: ss-enter 220ms cubic-bezier(0.16,1,0.3,1);
    z-index: 9999;
  }

  [data-theme="light"] .ss-dropdown {
    background: #FFFFFF;
    border-color: #E2E8F0;
    box-shadow:
      0 12px 40px rgba(0,0,0,0.12),
      0 4px 12px rgba(0,0,0,0.06);
  }

  @keyframes ss-enter {
    from {
      opacity: 0;
      transform: translateY(-6px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* ── Search bar ── */
  .ss-search {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 8px;
    border-bottom: 1px solid var(--color-border-default, rgba(255,255,255,0.06));
  }

  .ss-search-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 3px;
    background: var(--color-brand-glow, rgba(139,0,0,0.1));
    flex-shrink: 0;
    margin-right: 8px;
    transition: background 200ms ease;
  }
  .ss-search-icon {
    width: 14px;
    height: 14px;
    color: var(--color-brand-primary);
  }

  .ss-search-input {
    flex: 1;
    min-width: 0;
    padding: 8px 0;
    background: transparent;
    border: none;
    font-size: 14px;
    color: var(--color-text-primary);
    outline: none;
  }
  .ss-search-input::placeholder {
    color: var(--color-text-muted);
  }

  .ss-search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background: transparent;
    border: none;
    border-radius: 3px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 150ms ease;
    flex-shrink: 0;
  }
  .ss-search-clear:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }
  .ss-search-clear-x { width: 12px; height: 12px; }

  /* ── Options list ── */
  .ss-options {
    max-height: 260px;
    overflow-y: auto;
    padding: 4px;
    margin: 0;
    list-style: none;
  }

  .ss-options::-webkit-scrollbar { width: 5px; }
  .ss-options::-webkit-scrollbar-track { background: transparent; }
  .ss-options::-webkit-scrollbar-thumb {
    background: var(--color-brand-glow, rgba(139,0,0,0.25));
    border-radius: 3px;
  }
  [data-theme="light"] .ss-options::-webkit-scrollbar-thumb {
    background: rgba(26,95,122,0.2);
  }

  /* ── Single option ── */
  .ss-option {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 3px;
    cursor: pointer;
    transition: all 140ms cubic-bezier(0.16,1,0.3,1);
    animation: ss-item-in 200ms cubic-bezier(0.16,1,0.3,1) both;
  }

  @keyframes ss-item-in {
    from {
      opacity: 0;
      transform: translateX(-6px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .ss-option:hover,
  .ss-option.ss-highlighted {
    background: var(--color-bg-hover);
    transform: translateX(2px);
  }

  .ss-option.ss-selected-opt {
    background: var(--color-brand-glow, rgba(139,0,0,0.12));
  }

  [data-theme="light"] .ss-option.ss-selected-opt {
    background: rgba(26,95,122,0.08);
  }

  .ss-option.ss-opt-disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
  }

  /* ── Option content ── */
  .ss-opt-content {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  .ss-opt-icon-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: var(--color-bg-tertiary);
    border-radius: 3px;
    flex-shrink: 0;
    transition: background 140ms ease;
  }
  .ss-option:hover .ss-opt-icon-box,
  .ss-option.ss-highlighted .ss-opt-icon-box {
    background: var(--color-brand-glow, rgba(139,0,0,0.15));
  }
  [data-theme="light"] .ss-option:hover .ss-opt-icon-box,
  [data-theme="light"] .ss-option.ss-highlighted .ss-opt-icon-box {
    background: rgba(26,95,122,0.1);
  }

  .ss-opt-text {
    flex: 1;
    min-width: 0;
  }

  .ss-opt-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
    transition: color 140ms ease;
  }

  .ss-opt-desc {
    display: block;
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 1px;
  }

  /* ── Checkmark ── */
  .ss-opt-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: var(--color-brand-primary);
    border-radius: 50%;
    flex-shrink: 0;
    animation: ss-check-pop 250ms cubic-bezier(0.34,1.56,0.64,1);
  }
  .ss-check-icon {
    width: 12px;
    height: 12px;
    color: white;
  }

  @keyframes ss-check-pop {
    0% { transform: scale(0); }
    60% { transform: scale(1.15); }
    100% { transform: scale(1); }
  }

  /* ── Empty state ── */
  .ss-more-hint {
    padding: 8px 16px;
    text-align: center;
    font-size: 12px;
    font-style: italic;
    color: var(--color-text-muted);
    border-top: 1px solid var(--color-border-subtle);
    background: var(--color-bg-tertiary);
  }

  .ss-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px 16px;
    text-align: center;
    font-size: 13px;
    color: var(--color-text-muted);
  }
  .ss-empty-icon {
    width: 24px;
    height: 24px;
    opacity: 0.4;
  }

  /* ── Footer (result count) ── */
  .ss-footer {
    padding: 6px 12px;
    font-size: 11px;
    color: var(--color-text-muted);
    border-top: 1px solid var(--color-border-default, rgba(255,255,255,0.06));
    text-align: right;
  }

  /* ── Message ── */
  .ss-message {
    margin-top: 6px;
    font-size: 12px;
    color: var(--color-text-muted);
    transition: color 150ms ease;
  }
  .ss-msg-error {
    color: var(--color-error);
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .ss-trigger,
    .ss-trigger-accent,
    .ss-chevron,
    .ss-clear,
    .ss-option,
    .ss-opt-check,
    .ss-dropdown {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

export default SearchableSelect;
